import {
  areShapesEquivalent,
  extractEdgeVertices,
  resolveSharedEdgeShapes,
} from '../../common';
import { resolveFaceShapeBySelection } from '../../topology-selection-resolution';
import type { ChamferKernelInstance } from '../types/ChamferShapeBuildInput';
import { safeDelete } from './chamferPuppyKernel';
import {
  arePointsNear,
  computeEdgeMidpoint,
  EDGE_DISTANCE_EPS,
  measurePointDistance,
  normalizeVector,
  type Vector3Tuple,
} from './chamferVector';

type SurfaceAdaptor = {
  FirstUParameter: () => number;
  LastUParameter: () => number;
  FirstVParameter: () => number;
  LastVParameter: () => number;
  D1: (u: number, v: number, pointArg: unknown, tangentUArg: unknown, tangentVArg: unknown) => void;
  delete?: () => void;
};

/**
 * Face Face 共享边候选。
 */
export interface ResolvedFaceFaceSharedEdge {
  /** 共享边 Shape。 */
  edgeShape: unknown;
  /** Face Set 1 解析出的支撑面。 */
  leftFace: unknown;
  /** Face Set 1 面选择持久化引用。 */
  leftFaceRef: string;
  /** Face Set 2 解析出的支撑面。 */
  rightFace: unknown;
  /** Face Set 2 面选择持久化引用。 */
  rightFaceRef: string;
  /** 共享边端点。 */
  vertices: Vector3Tuple[];
}

/**
 * 解析 Face Face 倒角模式。
 *
 * @param definition 倒角定义。
 * @returns 归一化后的 Face Face 模式。
 */
export function resolveFaceFaceChamferMode(
  definition: ChamferFeatureDefinition,
): NonNullable<ChamferFeatureDefinition['faceFaceMode']> {
  return definition.faceFaceMode ?? (definition.symmetric === false ? 'asymmetric' : 'symmetric');
}

/**
 * 解析指定 Face Face 面组。
 *
 * @param definition 倒角定义。
 * @param setId 面组 ID。
 * @returns 面组选择项。
 */
function resolveFaceFaceSetItems(
  definition: ChamferFeatureDefinition,
  setId: 'faceSet1' | 'faceSet2',
): SelectionItemRef[] {
  return definition.faceSets?.find((faceSet) => faceSet.setId === setId)?.items ?? [];
}

/**
 * 校验并返回指定 Face Face 面组。
 *
 * @param definition 倒角定义。
 * @param setId 面组 ID。
 * @returns 已校验的面选择项。
 */
function resolveFaceFaceSelections(
  definition: ChamferFeatureDefinition,
  setId: 'faceSet1' | 'faceSet2',
): SelectionItemRef[] {
  const items = resolveFaceFaceSetItems(definition, setId);
  if (items.length === 0) {
    throw new Error(`Face Face Chamfer 缺少 ${setId} 面组选择`);
  }
  if (items.some((item) => item.kind !== 'face')) {
    throw new Error(`Face Face Chamfer 的 ${setId} 必须是面选择`);
  }
  return items;
}

/**
 * 判断两条共享边是否连通。
 *
 * @param left 左侧共享边。
 * @param right 右侧共享边。
 * @returns 是否连通。
 */
function areResolvedSharedEdgesConnected(
  left: ResolvedFaceFaceSharedEdge,
  right: ResolvedFaceFaceSharedEdge,
): boolean {
  return left.vertices.some((leftVertex) => (
    right.vertices.some((rightVertex) => arePointsNear(leftVertex, rightVertex))
  ));
}

/**
 * 按连通性分组共享边。
 *
 * @param sharedEdges 共享边集合。
 * @returns 连通分组。
 */
function groupResolvedFaceFaceSharedEdgesByConnectivity(
  sharedEdges: ResolvedFaceFaceSharedEdge[],
): ResolvedFaceFaceSharedEdge[][] {
  const visited = new Set<number>();
  const groups: ResolvedFaceFaceSharedEdge[][] = [];

  sharedEdges.forEach((candidate, startIndex) => {
    if (visited.has(startIndex)) {
      return;
    }

    const queue = [startIndex];
    const group: ResolvedFaceFaceSharedEdge[] = [];
    visited.add(startIndex);

    while (queue.length > 0) {
      const currentIndex = queue.shift();
      if (currentIndex === undefined) {
        continue;
      }
      const current = sharedEdges[currentIndex];
      if (!current) {
        continue;
      }
      group.push(current);

      sharedEdges.forEach((other, otherIndex) => {
        if (visited.has(otherIndex) || !areResolvedSharedEdgesConnected(current, other)) {
          return;
        }
        visited.add(otherIndex);
        queue.push(otherIndex);
      });
    }

    groups.push(group);
  });

  return groups;
}

/**
 * 计算共享边组到帮助点的距离。
 *
 * @param group 共享边组。
 * @param helpPoint 帮助点坐标。
 * @returns 最近距离。
 */
function resolveFaceFaceSharedEdgeGroupDistanceToHelpPoint(
  group: ResolvedFaceFaceSharedEdge[],
  helpPoint: Vector3Tuple,
): number {
  return group.reduce((nearestDistance, sharedEdge) => {
    const vertexDistance = sharedEdge.vertices.reduce((nearestVertexDistance, vertex) => (
      Math.min(nearestVertexDistance, measurePointDistance(vertex, helpPoint))
    ), Number.POSITIVE_INFINITY);
    const midpoint = computeEdgeMidpoint(sharedEdge.vertices);
    const midpointDistance = midpoint
      ? measurePointDistance(midpoint, helpPoint)
      : Number.POSITIVE_INFINITY;
    return Math.min(nearestDistance, vertexDistance, midpointDistance);
  }, Number.POSITIVE_INFINITY);
}

/**
 * 按帮助点过滤 Face Face 多解共享边。
 *
 * @param sharedEdges 共享边候选。
 * @param helpPoint 帮助点引用。
 * @returns 过滤后的共享边。
 */
export function filterResolvedFaceFaceSharedEdgesByHelpPoint(
  sharedEdges: ResolvedFaceFaceSharedEdge[],
  helpPoint: ChamferFeatureDefinition['helpPoint'],
): ResolvedFaceFaceSharedEdge[] {
  if (!helpPoint) {
    return sharedEdges;
  }
  const point = helpPoint.point;
  if (
    !Array.isArray(point)
    || point.length !== 3
    || point.some((value) => !Number.isFinite(value))
  ) {
    throw new Error('Face Face Chamfer 的 Help Point Vertex 缺少有效三维坐标');
  }

  const groups = groupResolvedFaceFaceSharedEdgesByConnectivity(sharedEdges);
  if (groups.length <= 1) {
    return sharedEdges;
  }

  /**
   * Help Point Vertex 只负责在 Face Face 多解场景里选定更靠近该顶点的倒角区域。
   * 这里不改变 Puppy CAD 内核 builder，只在求解前收窄共享边候选。
   */
  const [closestGroup] = groups
    .map((group) => ({
      group,
      distance: resolveFaceFaceSharedEdgeGroupDistanceToHelpPoint(group, point),
    }))
    .sort((left, right) => left.distance - right.distance);

  return closestGroup?.group ?? sharedEdges;
}

/**
 * 计算向量点积。
 *
 * @param left 左向量。
 * @param right 右向量。
 * @returns 点积。
 */
function dotVector(left: Vector3Tuple, right: Vector3Tuple): number {
  return left[0] * right[0] + left[1] * right[1] + left[2] * right[2];
}

/**
 * 从面中心采样单位法向。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param faceShape 面 Shape。
 * @returns 面中心单位法向。
 */
export function computeFaceFaceCenterNormal(
  puppy: ChamferKernelInstance,
  faceShape: unknown,
): Vector3Tuple {
  let surface: SurfaceAdaptor | null = null;
  let point: unknown = null;
  let tangentU: unknown = null;
  let tangentV: unknown = null;
  let normalVector: unknown = null;

  try {
    surface = new puppy.BRepAdaptor_Surface_2(faceShape as never, true) as SurfaceAdaptor;
    const uParam = (surface.FirstUParameter() + surface.LastUParameter()) / 2;
    const vParam = (surface.FirstVParameter() + surface.LastVParameter()) / 2;
    point = new puppy.gp_Pnt_3(0, 0, 0);
    tangentU = new puppy.gp_Vec_4(0, 0, 0);
    tangentV = new puppy.gp_Vec_4(0, 0, 0);
    surface.D1(uParam, vParam, point, tangentU, tangentV);
    normalVector = (tangentU as {
      Crossed: (other: unknown) => { X: () => number; Y: () => number; Z: () => number };
    }).Crossed(tangentV);
    return normalizeVector([
      (normalVector as { X: () => number }).X(),
      (normalVector as { Y: () => number }).Y(),
      (normalVector as { Z: () => number }).Z(),
    ]);
  } finally {
    safeDelete(normalVector);
    safeDelete(tangentV);
    safeDelete(tangentU);
    safeDelete(point);
    safeDelete(surface);
  }
}

/**
 * 将 SolidWorks 面-面倒角弦宽度换算为 Puppy CAD 内核 对称倒角距离。
 *
 * @param chordWidth 弦宽度。
 * @param leftNormal 第一面法向。
 * @param rightNormal 第二面法向。
 * @returns 等效对称倒角距离。
 */
export function resolveDistanceFromFaceFaceChamferChordWidth(
  chordWidth: number,
  leftNormal: Vector3Tuple,
  rightNormal: Vector3Tuple,
): number {
  if (!Number.isFinite(chordWidth) || chordWidth <= 0) {
    throw new Error('面-面倒角弦宽度必须大于 0');
  }
  const normalizedLeft = normalizeVector(leftNormal);
  const normalizedRight = normalizeVector(rightNormal);
  const normalAngle = Math.acos(Math.min(1, Math.max(-1, Math.abs(dotVector(normalizedLeft, normalizedRight)))));
  if (normalAngle <= EDGE_DISTANCE_EPS) {
    throw new Error('面-面倒角弦宽度模式无法从平行或重合法向面组换算距离');
  }
  return chordWidth / (2 * Math.sin(normalAngle / 2));
}

/**
 * 根据两侧面 Shape 将弦宽度换算为等效倒角距离。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param chordWidth 弦宽度。
 * @param leftFace Face Set 1 支撑面。
 * @param rightFace Face Set 2 支撑面。
 * @returns 等效对称倒角距离。
 */
export function resolveDistanceFromFaceFaceChamferChordWidthOnFaces(
  puppy: ChamferKernelInstance,
  chordWidth: number,
  leftFace: unknown,
  rightFace: unknown,
): number {
  return resolveDistanceFromFaceFaceChamferChordWidth(
    chordWidth,
    computeFaceFaceCenterNormal(puppy, leftFace),
    computeFaceFaceCenterNormal(puppy, rightFace),
  );
}

/**
 * 收集 Face Set 1/2 之间的共享边。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param sourceShape 上游实体。
 * @param sourceMetadata 上游元数据。
 * @param definition 倒角定义。
 * @returns 共享边集合。
 */
export function collectResolvedFaceFaceSharedEdges(
  puppy: ChamferKernelInstance,
  sourceShape: unknown,
  sourceMetadata: Record<string, unknown>,
  sourceObjectId: string,
  definition: ChamferFeatureDefinition,
): ResolvedFaceFaceSharedEdge[] {
  const faceSet1 = resolveFaceFaceSelections(definition, 'faceSet1');
  const faceSet2 = resolveFaceFaceSelections(definition, 'faceSet2');
  const leftFaces = faceSet1.map((item) => ({
    item,
    faceShape: resolveFaceShapeBySelection(puppy, sourceShape, sourceMetadata, item, { sourceObjectId }),
  }));
  const rightFaces = faceSet2.map((item) => ({
    item,
    faceShape: resolveFaceShapeBySelection(puppy, sourceShape, sourceMetadata, item, { sourceObjectId }),
  }));

  const resolvedEdges: ResolvedFaceFaceSharedEdge[] = [];

  leftFaces.forEach((leftFace) => {
    rightFaces.forEach((rightFace) => {
      resolveSharedEdgeShapes(puppy, leftFace.faceShape, rightFace.faceShape).forEach((edgeShape) => {
        if (resolvedEdges.some((candidate) => areShapesEquivalent(candidate.edgeShape, edgeShape))) {
          return;
        }
        resolvedEdges.push({
          edgeShape,
          leftFace: leftFace.faceShape,
          leftFaceRef: leftFace.item.persistentRef,
          rightFace: rightFace.faceShape,
          rightFaceRef: rightFace.item.persistentRef,
          vertices: extractEdgeVertices(puppy, edgeShape),
        });
      });
    });
  });

  return resolvedEdges;
}
